/**
 * Auth Store
 * Zustand store for authentication state
 */

import { bookingDraftKey, clearBookingDraft } from "../utils/booking-draft";
import { clearReads } from "../offline/reads";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface User {
  id: string;
  /** Server-managed company membership, independent of booking partner. */
  tenantId?: string;
  email: string;
  name: string;
  role: "admin" | "partner" | "crew";
  partnerId?: string;
  partnerName?: string;
}

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: User | null;
  isAuthenticated: boolean;

  // Actions
  login: (token: string, user: User, refreshToken?: string) => void;
  setToken: (token: string) => void;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,

      login: (token, user, refreshToken) => {
        set({
          token,
          refreshToken: refreshToken ?? null,
          user,
          isAuthenticated: true,
        });
      },

      setToken: (token) => {
        set({ token });
      },

      logout: () => {
        // what was kept on the device for this user is removed with the session (the queue of unsent entries is not)
        const leavingUser = useAuthStore.getState().user;
        clearBookingDraft(bookingDraftKey(leavingUser));
        const leaving = leavingUser?.id;
        if (leaving) void clearReads(leaving);
        set({
          token: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
        });
      },

      updateUser: (updates) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        }));
      },
    }),
    {
      name: "seaslot-auth",
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
